import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, CalendarClock, TrendingUp, TrendingDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Finestra temporale di default mostrata nello scadenziario (giorni dall'oggi).
// Coerente con il valore "1 Mese" del Select. I valori alternativi sono offerti
// nel Select stesso (7 / 15 / 30 / 60 / 90 / 365).
const DEFAULT_DAYS_AHEAD = 30;

export default function ScadenziarioPage() {
  const [data, setData] = useState({ items: [], totale_incassi: 0, totale_uscite: 0 });
  const [days, setDays] = useState(DEFAULT_DAYS_AHEAD);
  const [filter, setFilter] = useState('tutti'); // tutti|incasso|uscita
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fatturazione/scadenziario?days=${days}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Errore caricamento scadenziario:', err);
      toast.error('Errore caricamento scadenziario');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const items = data.items.filter(i => filter === 'tutti' || i.tipo === filter);
  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

  const getGiorniBadge = (giorni) => {
    if (giorni < 0) return <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline"><AlertTriangle className="h-3 w-3 mr-1" />Scaduta {Math.abs(giorni)}gg fa</Badge>;
    if (giorni === 0) return <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="outline">Oggi</Badge>;
    if (giorni <= 7) return <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="outline">{giorni}gg</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 border-slate-200" variant="outline">{giorni}gg</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="scadenziario-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-6 w-6" />Scadenziario
          </h1>
          <p className="text-sm text-slate-500 mt-1">Tutte le scadenze di incasso e pagamento</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={v => setDays(parseInt(v))}>
            <SelectTrigger className="w-44" data-testid="scadenziario-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Prossimi 7 giorni</SelectItem>
              <SelectItem value="15">Prossimi 15 giorni</SelectItem>
              <SelectItem value="30">Prossimi 30 giorni</SelectItem>
              <SelectItem value="60">Prossimi 60 giorni</SelectItem>
              <SelectItem value="90">Prossimi 90 giorni</SelectItem>
              <SelectItem value="365">Prossimo anno</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="scadenziario-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              <SelectItem value="incasso">Solo incassi</SelectItem>
              <SelectItem value="uscita">Solo uscite</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase font-medium flex items-center gap-1"><TrendingUp className="h-3 w-3" />Da Incassare</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="tot-incassi">€ {fmt(data.totale_incassi)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-xs text-red-600 uppercase font-medium flex items-center gap-1"><TrendingDown className="h-3 w-3" />Da Pagare</p>
            <p className="text-2xl font-bold text-red-700 mt-1" data-testid="tot-uscite">€ {fmt(data.totale_uscite)}</p>
          </CardContent>
        </Card>
        <Card className={`${(data.totale_incassi - data.totale_uscite) >= 0 ? 'border-blue-200 bg-blue-50' : 'border-orange-200 bg-orange-50'}`}>
          <CardContent className="p-4">
            <p className={`text-xs uppercase font-medium ${(data.totale_incassi - data.totale_uscite) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo Previsto</p>
            <p className={`text-2xl font-bold mt-1 ${(data.totale_incassi - data.totale_uscite) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>€ {fmt(data.totale_incassi - data.totale_uscite)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Tipo</TableHead>
                <TableHead className="text-xs uppercase">Protocollo</TableHead>
                <TableHead className="text-xs uppercase">N. Doc</TableHead>
                <TableHead className="text-xs uppercase">Controparte</TableHead>
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Scadenza</TableHead>
                <TableHead className="text-xs uppercase text-right">Importo</TableHead>
                <TableHead className="text-xs uppercase">Giorni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Nessuna scadenza nel periodo selezionato</TableCell></TableRow>
              ) : items.map((row) => (
                <TableRow key={row.id} data-testid={`scad-row-${row.id}`}>
                  <TableCell>
                    {row.tipo === 'incasso' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">Incasso</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline">Uscita</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.protocollo || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{row.numero}</TableCell>
                  <TableCell className="font-medium">{row.controparte}</TableCell>
                  <TableCell className="text-sm text-slate-600">{row.data ? format(parseISO(row.data), 'dd-MM-yyyy') : '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{row.scadenza ? format(parseISO(row.scadenza), 'dd-MM-yyyy', { locale: it }) : '-'}</TableCell>
                  <TableCell className={`text-right font-semibold ${row.tipo === 'incasso' ? 'text-emerald-700' : 'text-red-700'}`}>€ {fmt(row.importo)}</TableCell>
                  <TableCell>{getGiorniBadge(row.giorni)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
